'use strict';

import {
    DEFAULT_SURFACE_RADIUS,
    FOCUS_AT_RATIO,
    MAIN_BAR_RETURN_MS,
    animateHeightSpring,
    dismissContent,
    fadeIn,
    fadeOut,
    prefersReducedMotion,
    revealContent,
    visualRadius,
} from './mobile-floating-bar-morph';

const DISPLAY_FLEX = 'flex';
const DISPLAY_NONE = 'none';
const MOBILE_BREAKPOINT = 767;
const BACK_BTN_CLASS = 'mobile-floating-bar-back-btn';
const FADE_CLASS = 'fade';
const VIEW_OPENED_EVENT = 'mfb:view-opened';
const KEYBOARD_INSET_PROPERTY = '--mfb-keyboard-inset';
const BG_CLASS = 'mobile-floating-bar-surface-bg';
const HAS_MODAL_CLASS = 'mobile-floating-bar-has-modal';
const MORPH_CLASS = 'mobile-floating-bar-surface-morphing';
const MODAL_MORPH_CLASS = 'mobile-floating-bar-modal-morphing';
const BAR_SELECTOR = '.mobile-floating-control-bar';
const MAIN_SELECTOR = '.mobile-control-bar-main';
const VIEW_SELECTOR = '.mobile-control-bar-view';
const MODAL_BOTTOM_SHADOW_CLASS = 'mobile-floating-bar-show-bottom-shadow';
const MODAL_TOP_SHADOW_CLASS = 'mobile-floating-bar-show-top-shadow';

const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
const controllers = new Map();
let keyboardTrackedBar = null;
let keyboardReleased = false;
let keyboardFrame = 0;
const collapsingModals = new WeakSet();
const modalScrollDepthCleanupByModal = new WeakMap();

function claimDocumentBinding(flag) {
    if (document.documentElement.dataset[flag] === '1') return false;
    document.documentElement.dataset[flag] = '1';
    return true;
}

function isDisplayed(el) {
    return window.getComputedStyle(el).display !== 'none';
}

function visibleSurface(bar) {
    const surfaces = Array.from(bar.querySelectorAll(`${MAIN_SELECTOR}, ${VIEW_SELECTOR}`));
    return surfaces.find(isDisplayed) || null;
}

function rememberMainBarHeight(bar, height) {
    if (height > 0) bar.dataset.mfbMainHeight = String(height);
}

function ensureSurfaceBg(surface) {
    const existing = surface.querySelector(`:scope > .${BG_CLASS}`);
    if (existing) return existing;
    const bg = document.createElement('div');
    bg.className = BG_CLASS;
    bg.setAttribute('aria-hidden', 'true');
    surface.insertBefore(bg, surface.firstChild);
    return bg;
}

function cancelBarAnimations(bar) {
    bar.getAnimations({ subtree: true }).forEach((anim) => anim.cancel());
}

function clearMorphState(bar) {
    bar.classList.remove(MORPH_CLASS);
    cancelBarAnimations(bar);
    bar.querySelectorAll(`${MAIN_SELECTOR}, ${VIEW_SELECTOR}`).forEach((el) => {
        el.removeAttribute('style');
    });
    bar.style.removeProperty('height');
    delete bar.dataset.mfbTransitioning;
}

function applySurface(mainBar, viewBars, active) {
    mainBar.style.display = active === mainBar ? DISPLAY_FLEX : DISPLAY_NONE;
    viewBars.forEach((view) => {
        if (view === active) {
            view.classList.remove('d-none');
            view.style.display = DISPLAY_FLEX;
        } else {
            view.classList.add('d-none');
            view.style.removeProperty('display');
        }
    });
}

function hideViews(bar) {
    bar.querySelectorAll(VIEW_SELECTOR).forEach((view) => {
        view.classList.add('d-none');
        view.style.removeProperty('display');
    });
}

function contentChildren(surface) {
    return Array.from(surface.children).filter((child) => !child.classList.contains(BG_CLASS));
}

function runSurfaceMorph(outgoing, incoming, outRect, inRect) {
    const { animation, duration } = animateHeightSpring(incoming, {
        fromHeight: outRect.height,
        toHeight: inRect.height,
        fromRadius: visualRadius(outgoing, outRect.height),
        toRadius: visualRadius(incoming, inRect.height),
    });
    const revealed = contentChildren(incoming).map((child) => revealContent(child, duration));
    const finished = settled([animation, fadeOut(outgoing, duration), ...revealed]);
    return { finished, duration };
}

function createSurfaceController(mainBar, floatingBar, viewBars) {
    function goInstant(active) {
        clearMorphState(floatingBar);
        applySurface(mainBar, viewBars, active);
    }

    function goMorph(incoming) {
        const outgoing = visibleSurface(floatingBar);
        if (incoming === outgoing) return 0;
        if (!outgoing || prefersReducedMotion()) {
            goInstant(incoming);
            return 0;
        }
        if (floatingBar.dataset.mfbTransitioning === '1') return 0;
        floatingBar.dataset.mfbTransitioning = '1';
        floatingBar.classList.add(MORPH_CLASS);

        ensureSurfaceBg(outgoing);
        ensureSurfaceBg(incoming);

        if (incoming !== mainBar) incoming.classList.remove('d-none');
        incoming.style.display = DISPLAY_FLEX;
        incoming.style.zIndex = '1';

        const outRect = outgoing.getBoundingClientRect();
        const inRect = incoming.getBoundingClientRect();

        if (outgoing === mainBar) rememberMainBarHeight(floatingBar, outRect.height);
        if (incoming === mainBar) rememberMainBarHeight(floatingBar, inRect.height);

        outgoing.style.zIndex = '2';
        outgoing.style.position = 'absolute';
        outgoing.style.left = '0';
        outgoing.style.right = '0';
        outgoing.style.bottom = '0';

        const { finished, duration } = runSurfaceMorph(outgoing, incoming, outRect, inRect);
        finished.then(() => {
            clearMorphState(floatingBar);
            applySurface(mainBar, viewBars, incoming);
        });
        return duration;
    }

    return {
        showMain: () => goMorph(mainBar),
        showView: (viewId) => {
            const view = document.getElementById(viewId);
            return view ? goMorph(view) : 0;
        },
    };
}

function getController(barId) {
    const entry = controllers.get(barId);
    if (!entry) return null;
    if (!document.contains(entry.element)) {
        controllers.delete(barId);
        return null;
    }
    return entry.controller;
}

function showMainBar(bar) {
    const ctrl = bar.id ? getController(bar.id) : null;
    if (ctrl) {
        ctrl.showMain();
        return;
    }
    const mainBar = bar.querySelector(MAIN_SELECTOR);
    if (!mainBar) return;
    clearMorphState(bar);
    mainBar.style.display = DISPLAY_FLEX;
    hideViews(bar);
}

function keyboardOverlap() {
    const viewport = window.visualViewport;
    return Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
}

function clearKeyboardInset() {
    if (!keyboardTrackedBar) return;
    keyboardTrackedBar.style.removeProperty(KEYBOARD_INSET_PROPERTY);
    keyboardTrackedBar = null;
}

function applyKeyboardInset() {
    keyboardFrame = 0;
    if (!keyboardTrackedBar) return;
    const overlap = keyboardOverlap();
    if (keyboardReleased && overlap === 0) {
        clearKeyboardInset();
        return;
    }
    keyboardTrackedBar.style.setProperty(KEYBOARD_INSET_PROPERTY, `${overlap}px`);
}

function scheduleKeyboardInset() {
    if (keyboardFrame || !keyboardTrackedBar) return;
    keyboardFrame = window.requestAnimationFrame(applyKeyboardInset);
}

function barOfFocusedElement(element) {
    if (!(element instanceof Element)) return null;
    return element.closest(BAR_SELECTOR);
}

function bindKeyboardInsetOnce() {
    if (!window.visualViewport) return;
    if (!claimDocumentBinding('mfbKeyboardInset')) return;

    document.addEventListener('focusin', (event) => {
        const bar = mobileQuery.matches ? barOfFocusedElement(event.target) : null;
        if (!bar) {
            keyboardReleased = true;
            scheduleKeyboardInset();
            return;
        }
        if (bar !== keyboardTrackedBar) clearKeyboardInset();
        keyboardTrackedBar = bar;
        keyboardReleased = false;
        scheduleKeyboardInset();
    });

    document.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
            if (barOfFocusedElement(document.activeElement)) return;
            keyboardReleased = true;
            scheduleKeyboardInset();
        });
    });

    window.visualViewport.addEventListener('resize', scheduleKeyboardInset, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleKeyboardInset, { passive: true });
}

function bindOutsideClickOnce() {
    if (!claimDocumentBinding('mfbOutsideClick')) return;
    document.addEventListener('click', (event) => {
        if (!mobileQuery.matches) return;
        const target = event.target;
        if (!(target instanceof Element) || target.closest('.topBar')) return;

        document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
            if (barHasVisibleModal(bar)) return;
            const mainBar = bar.querySelector(MAIN_SELECTOR);
            if (!mainBar || isDisplayed(mainBar)) return;
            if (!bar.contains(target)) showMainBar(bar);
        });
    });
}

function initializeMobileFloatingBar(barId) {
    const mainBar = document.getElementById(barId + 'Main');
    if (!mainBar) return;
    const floatingBar = mainBar.parentElement;
    if (!floatingBar) return;

    const existing = controllers.get(barId);
    if (existing && existing.element === floatingBar) return;

    bindOutsideClickOnce();
    bindKeyboardInsetOnce();

    const viewBars = Array.from(floatingBar.children).filter(
        (el) => el !== mainBar && el.classList.contains('mobile-control-bar-view')
    );

    ensureSurfaceBg(mainBar);
    viewBars.forEach(ensureSurfaceBg);
    if (isDisplayed(mainBar)) rememberMainBarHeight(floatingBar, mainBar.getBoundingClientRect().height);

    const controller = createSurfaceController(mainBar, floatingBar, viewBars);
    controllers.set(barId, { element: floatingBar, controller });
}

function resolveViewFocusTarget(viewConfig) {
    if (!viewConfig.focusSelector) return null;
    const scope = viewConfig.viewId.startsWith('#') ? viewConfig.viewId : '#' + viewConfig.viewId;
    return document.querySelector(`${scope} ${viewConfig.focusSelector}`)
        || document.querySelector(viewConfig.focusSelector);
}

function announceViewOpened(viewId) {
    const view = document.getElementById(viewId);
    if (!view) return;
    view.dispatchEvent(new CustomEvent(VIEW_OPENED_EVENT, { bubbles: true }));
}

function runViewAfterOpen(viewConfig) {
    const focusEl = resolveViewFocusTarget(viewConfig);
    if (focusEl) focusEl.focus();
    if (typeof viewConfig.afterOpen === 'function') viewConfig.afterOpen(viewConfig.viewId);
    announceViewOpened(viewConfig.viewId);
}

function bindViewButton(viewConfig, showView) {
    const btn = document.getElementById(viewConfig.btnId);
    if (!btn) return;
    btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const duration = showView(viewConfig.viewId);
        window.setTimeout(() => runViewAfterOpen(viewConfig), Math.round(duration * FOCUS_AT_RATIO));
    });
}

function bindBackButton(btnId, showMain) {
    const btn = document.getElementById(btnId);
    if (!btn) return;
    btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        showMain();
    });
}

function bindMobileFloatingBar(barId, options) {
    if (!document.getElementById(barId)) return;
    setupModalHandlers();
    initializeMobileFloatingBar(barId);
    const ctrl = getController(barId);
    if (!ctrl) return;

    (options.views || []).forEach((viewConfig) => bindViewButton(viewConfig, ctrl.showView));
    (options.backBtnIds || []).forEach((btnId) => bindBackButton(btnId, ctrl.showMain));
}

function detachModalsFromBar(bar, keep = null) {
    bar.querySelectorAll('.modal').forEach((modal) => {
        unbindModalScrollDepth(modal);
        clearModalMorphStyles(modal);
        if (modal === keep) return;
        const backBtn = modal.querySelector(`.${BACK_BTN_CLASS}`);
        if (backBtn) backBtn.remove();
        restoreModalFade(modal);
        document.body.appendChild(modal);
    });
}

function barHasVisibleModal(bar) {
    return Array.from(bar.querySelectorAll('.modal')).some(
        (modal) => modal.classList.contains('show') || modal.style.display === 'block'
    );
}

function setModalShadow(bar, className, visible) {
    bar.classList.toggle(className, visible);
}

function clearModalShadows(bar) {
    setModalShadow(bar, MODAL_TOP_SHADOW_CLASS, false);
    setModalShadow(bar, MODAL_BOTTOM_SHADOW_CLASS, false);
}

function updateModalScrollShadows(modal) {
    const bar = modal.closest(BAR_SELECTOR);
    if (!bar) return;
    const body = modal.querySelector('.modal-body');
    if (!body) {
        clearModalShadows(bar);
        return;
    }
    const hasMoreAbove = body.scrollTop > 1;
    const hasMoreBelow = body.scrollHeight - body.clientHeight > 1
        && body.scrollTop + body.clientHeight < body.scrollHeight - 1;
    setModalShadow(bar, MODAL_TOP_SHADOW_CLASS, hasMoreAbove);
    setModalShadow(bar, MODAL_BOTTOM_SHADOW_CLASS, hasMoreBelow);
}

function unbindModalScrollDepth(modal) {
    const cleanup = modalScrollDepthCleanupByModal.get(modal);
    if (cleanup) cleanup();
    modalScrollDepthCleanupByModal.delete(modal);
    const bar = modal.closest(BAR_SELECTOR);
    if (bar) clearModalShadows(bar);
}

function bindModalScrollDepth(modal) {
    unbindModalScrollDepth(modal);
    const body = modal.querySelector('.modal-body');
    if (!body) return;

    let frame = 0;
    const onChange = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
            frame = 0;
            updateModalScrollShadows(modal);
        });
    };

    body.addEventListener('scroll', onChange, { passive: true });
    window.addEventListener('resize', onChange, { passive: true });

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(onChange);
        resizeObserver.observe(body);
    }

    modalScrollDepthCleanupByModal.set(modal, () => {
        if (frame) window.cancelAnimationFrame(frame);
        body.removeEventListener('scroll', onChange);
        window.removeEventListener('resize', onChange);
        if (resizeObserver) resizeObserver.disconnect();
    });

    onChange();
}

function releaseBodyModalState() {
    if (document.querySelector(`${BAR_SELECTOR}.${HAS_MODAL_CLASS}`)) return;
    document.body.classList.remove(HAS_MODAL_CLASS);
}

function resetFloatingBarModalState(bar) {
    detachModalsFromBar(bar);
    bar.classList.remove(HAS_MODAL_CLASS);
    bar.classList.remove(MODAL_MORPH_CLASS);
    clearModalShadows(bar);
    delete bar.dataset.mfbOriginHeight;
    delete bar.dataset.mfbOriginRadius;
    delete bar.dataset.mfbPendingExpand;
    showMainBar(bar);
    animateMainBarReturn(bar);
}

function cleanupStuckModalStates() {
    document.querySelectorAll(`${BAR_SELECTOR}.${HAS_MODAL_CLASS}`).forEach((bar) => {
        if (!barHasVisibleModal(bar)) resetFloatingBarModalState(bar);
    });
    releaseBodyModalState();
}

function addModalBackButton(modal) {
    const header = modal.querySelector('.modal-header');
    if (!header || header.querySelector(`.${BACK_BTN_CLASS}`)) return;
    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = BACK_BTN_CLASS;
    backBtn.setAttribute('data-dismiss', 'modal');
    backBtn.setAttribute('aria-label', 'Back');
    const icon = document.createElement('i');
    icon.className = 'fa-solid fa-arrow-left';
    backBtn.appendChild(icon);
    header.insertBefore(backBtn, header.firstChild);
}

function findMobileBarForModal(modal) {
    const bars = document.querySelectorAll(BAR_SELECTOR);
    for (let i = 0; i < bars.length; i++) {
        const bar = bars[i];
        if (modal.contains(bar)) continue;
        return bar;
    }
    return null;
}

function suspendModalFade(modal) {
    if (!modal.classList.contains(FADE_CLASS)) return;
    modal.classList.remove(FADE_CLASS);
    modal.dataset.mfbFadeSuspended = '1';
}

function restoreModalFade(modal) {
    if (modal.dataset.mfbFadeSuspended !== '1') return;
    delete modal.dataset.mfbFadeSuspended;
    modal.classList.add(FADE_CLASS);
}

function modalMorphParts(modal) {
    return {
        dialog: modal.querySelector('.modal-dialog'),
        content: modal.querySelector('.modal-content'),
    };
}

function freezeModalHeight(modal, height) {
    const { dialog } = modalMorphParts(modal);
    if (dialog) dialog.style.height = `${height}px`;
}

function clearModalMorphStyles(modal) {
    const { dialog, content } = modalMorphParts(modal);
    if (dialog) dialog.style.removeProperty('height');
    if (content) {
        content.style.removeProperty('opacity');
        content.getAnimations().forEach((anim) => anim.cancel());
    }
}

function pinBarToOrigin(bar, modal, originHeight) {
    const { content } = modalMorphParts(modal);
    bar.classList.add(MODAL_MORPH_CLASS);
    bar.style.height = `${originHeight}px`;
    if (content) content.style.opacity = '0';
}

function unpinBar(bar, modal) {
    bar.classList.remove(MODAL_MORPH_CLASS);
    bar.style.removeProperty('height');
    bar.getAnimations().forEach((animation) => animation.cancel());
    if (modal) clearModalMorphStyles(modal);
}

function settled(animations) {
    return Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
}

function barHeightHeadroom(bar, toHeight) {
    const ceiling = parseFloat(getComputedStyle(bar).maxHeight);
    return Number.isFinite(ceiling) ? ceiling - toHeight : Infinity;
}

function animateBarExpand(bar) {
    if (bar.dataset.mfbPendingExpand !== '1') return;
    delete bar.dataset.mfbPendingExpand;

    const modal = bar.querySelector('.modal');
    if (!modal) {
        unpinBar(bar, null);
        return;
    }

    const fromHeight = parseFloat(bar.dataset.mfbOriginHeight || '0');
    const fromRadius = parseFloat(bar.dataset.mfbOriginRadius || String(DEFAULT_SURFACE_RADIUS));
    bar.style.removeProperty('height');
    const toHeight = bar.getBoundingClientRect().height;
    if (!fromHeight || prefersReducedMotion() || toHeight - fromHeight < 2) {
        unpinBar(bar, modal);
        bindModalScrollDepth(modal);
        return;
    }

    const { content } = modalMorphParts(modal);
    freezeModalHeight(modal, toHeight);
    if (content) content.style.removeProperty('opacity');

    const { animation, duration } = animateHeightSpring(bar, {
        fromHeight,
        toHeight,
        fromRadius,
        toRadius: visualRadius(bar, toHeight),
        headroom: barHeightHeadroom(bar, toHeight),
    });
    const running = [animation];
    if (content) running.push(revealContent(content, duration));

    settled(running).then(() => {
        if (!bar.contains(modal)) return;
        unpinBar(bar, modal);
        bindModalScrollDepth(modal);
    });
}

function animateBarCollapse(bar, modal) {
    unbindModalScrollDepth(modal);
    const mainBar = bar.querySelector(MAIN_SELECTOR);
    const toHeight = parseFloat(bar.dataset.mfbMainHeight || bar.dataset.mfbOriginHeight || '0');
    const fromHeight = bar.getBoundingClientRect().height;
    if (!toHeight || prefersReducedMotion() || fromHeight - toHeight < 2) return Promise.resolve();

    const { content } = modalMorphParts(modal);
    const fromRadius = visualRadius(bar, fromHeight);
    const toRadius = mainBar ? visualRadius(mainBar, toHeight) : DEFAULT_SURFACE_RADIUS;

    freezeModalHeight(modal, fromHeight);
    bar.classList.add(MODAL_MORPH_CLASS);

    const { animation, duration } = animateHeightSpring(bar, {
        fromHeight,
        toHeight,
        fromRadius,
        toRadius,
    });
    const running = [animation];
    if (content) running.push(dismissContent(content, duration));

    return settled(running);
}

function animateMainBarReturn(bar) {
    if (prefersReducedMotion()) return;
    const mainBar = bar.querySelector(MAIN_SELECTOR);
    if (mainBar) fadeIn(mainBar, MAIN_BAR_RETURN_MS);
}

function moveModalIntoBar(modal) {
    if (modal.closest(BAR_SELECTOR)) return;
    const bar = findMobileBarForModal(modal);
    if (!bar) return;
    const replacingSheet = bar.classList.contains(HAS_MODAL_CLASS);
    clearMorphState(bar);
    const origin = replacingSheet ? null : visibleSurface(bar);
    const originHeight = origin
        ? origin.getBoundingClientRect().height
        : parseFloat(bar.dataset.mfbOriginHeight || '0');
    const originRadius = origin
        ? visualRadius(origin, originHeight)
        : parseFloat(bar.dataset.mfbOriginRadius || String(DEFAULT_SURFACE_RADIUS));
    bar.dataset.mfbOriginHeight = String(originHeight);
    bar.dataset.mfbOriginRadius = String(originRadius);
    detachModalsFromBar(bar, modal);
    const mainBar = bar.querySelector(MAIN_SELECTOR);
    if (mainBar) mainBar.style.display = DISPLAY_NONE;
    hideViews(bar);
    suspendModalFade(modal);
    bar.appendChild(modal);
    bar.classList.add(HAS_MODAL_CLASS);
    document.body.classList.add(HAS_MODAL_CLASS);
    if (!replacingSheet && !prefersReducedMotion()) {
        bar.dataset.mfbPendingExpand = '1';
        pinBarToOrigin(bar, modal, originHeight);
    } else {
        bindModalScrollDepth(modal);
    }
    addModalBackButton(modal);
}

function ensureModalInFloatingBar(modal) {
    if (!mobileQuery.matches) {
        releaseBodyModalState();
        return;
    }
    if (modal.closest(BAR_SELECTOR)) {
        addModalBackButton(modal);
        return;
    }
    moveModalIntoBar(modal);
}

function restoreBodyAfterModal(modal) {
    const bar = modal.closest(BAR_SELECTOR);
    unbindModalScrollDepth(modal);
    clearModalMorphStyles(modal);
    if (!bar) return;
    const backBtn = modal.querySelector(`.${BACK_BTN_CLASS}`);
    if (backBtn) backBtn.remove();
    document.body.appendChild(modal);
    if (barHasVisibleModal(bar)) return;
    resetFloatingBarModalState(bar);
}

function setupModalHandlers() {
    if (typeof $ === 'undefined') {
        console.error('setupModalHandlers: jQuery is not loaded');
        return;
    }
    if (!claimDocumentBinding('mfbModalHandlers')) return;

    $(document).on('show.bs.modal', '.modal', function onModalShow() {
        ensureModalInFloatingBar(this);
    });

    $(document).on('shown.bs.modal', '.modal', function onModalShown() {
        ensureModalInFloatingBar(this);
        const bar = this.closest(BAR_SELECTOR);
        if (bar) animateBarExpand(bar);
    });

    $(document).on('hide.bs.modal', '.modal', function onModalHide(event) {
        const bar = this.closest(BAR_SELECTOR);
        if (!bar || !mobileQuery.matches) return;
        if (collapsingModals.has(this)) {
            collapsingModals.delete(this);
            return;
        }
        event.preventDefault();
        collapsingModals.add(this);
        const modal = this;
        animateBarCollapse(bar, modal).then(() => {
            modal.style.display = DISPLAY_NONE;
            restoreBodyAfterModal(modal);
            $(modal).modal('hide');
        });
    });

    $(document).on('hidden.bs.modal', '.modal', function onModalHidden() {
        restoreBodyAfterModal(this);
        restoreModalFade(this);
        releaseBodyModalState();
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') cleanupStuckModalStates();
    });
    window.addEventListener('pageshow', cleanupStuckModalStates);
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') cleanupStuckModalStates();
    });
}

function initAllFloatingBars() {
    document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
        if (bar.id) initializeMobileFloatingBar(bar.id);
    });
    setupModalHandlers();
}

export { initializeMobileFloatingBar, bindMobileFloatingBar };

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFloatingBars);
} else {
    initAllFloatingBars();
}
